/**
 * useStockModule - Fonte única da flag da Gestão Rastreador
 *
 * Lê `stock_enabled` via RPC `get_stock_module_settings` e expõe a mutation de
 * ativação (somente administradores, validado no banco).
 *
 * RELEASE: 2026-10-10 v1.1.0 - Atualiza as mensagens visíveis para Gestão Rastreador.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (Fase 2 do Estoque).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('useStockModule');

/** Hook completo (flag + ativação) */
export function useStockModule() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['stock-module', tenantId],
    enabled: !!tenantId,
    staleTime: 60_000,
    queryFn: async () => {
      // `.call(supabase, ...)` preserva o `this` do cliente
      const { data, error } = await (supabase.rpc as any).call(supabase, 'get_stock_module_settings', {
        p_tenant_hint: tenantId ?? null,
      });
      if (error) {
        logger.error('Erro ao ler flag do módulo Gestão Rastreador', { error: error.message });
        throw error;
      }
      return Boolean((data as { stock_enabled?: boolean } | null)?.stock_enabled);
    },
  });

  const toggleModule = useMutation({
    mutationFn: async (enabled: boolean) => {
      const { error } = await (supabase.rpc as any).call(supabase, 'update_stock_module_settings', {
        p_enabled: enabled,
        p_tenant_hint: tenantId ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['stock-module'] });
      toast.success(enabled ? 'Módulo Gestão Rastreador habilitado' : 'Módulo Gestão Rastreador desabilitado');
    },
    onError: (e: Error) => toast.error(e.message || 'Não foi possível alterar o módulo'),
  });

  return { isEnabled: query.data ?? false, isLoading: query.isLoading, toggleModule };
}

/** Atalho usado pelos gates de navegação */
export function useStockModuleEnabled(): boolean {
  return useStockModule().isEnabled;
}
